import { Subject, switchMap, timer } from 'rxjs';
import { Location } from '@angular/common';

import {
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    TrackByFunction,
    ViewEncapsulation
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PanelLayout, SbGrid, SbLayout } from '@models';
import {
    SbEvent,
    SbEventBusService,
    SbEventTopic,
    SbGridService,
    SbPrintService
} from '../services';
import { UiQuery } from '../../shared/state/ui/ui.query';
import { debounceTime, takeUntil, takeWhile } from 'rxjs/operators';
import { AppConfigQuery } from '../../core/state/app-config/app-config.query';
import { ToastService } from '../../shared/services/toast/toast.service';

@Component({
    selector: 'app-sb-grid',
    templateUrl: './sb-grid.component.html',
    styleUrls: ['./sb-grid.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class SbGridComponent implements OnInit, OnDestroy {
    gridPath: string;
    grid: SbGrid;
    isDataLoaded = false;
    isSearchDone: boolean;
    printLayout$ = this.printService.isPrinting$;
    isEnabledPrinting$ = this.printService.isEnabledPrinting$;
    unsubscribe$ = new Subject();
    useLegacyAccountList = this._appConfig.useLegacyAccountList;
    dashboardNotice = this._appConfig.appConfig.dashboardNotice;

    constructor(
        private route: ActivatedRoute,
        private gridService: SbGridService,
        private eventBus: SbEventBusService,
        private changeDetector: ChangeDetectorRef,
        private router: Router,
        private printService: SbPrintService,
        private location: Location,
        private uiQuery: UiQuery,
        private _appConfig: AppConfigQuery,
        private _toastService: ToastService
    ) {}

    experimentalFeatures = JSON.parse(sessionStorage.getItem('sbExperimental') || '{}');
    ngOnInit() {
        this.route.params.pipe(takeUntil(this.unsubscribe$)).subscribe((params) => {
            // this.route.pathFromRoot;
            this.gridPath = params['gridPath'];

            this.gridService.getGrid(this.gridPath).then(
                (g) => {
                    this.grid = g;

                    // ...if we're on the "old" dashboard and there's a notice to show...
                    if (
                        this.grid.gridPath === 'assets-and-liabilities-overview' &&
                        this.dashboardNotice
                    ) {
                        // ...handle the notice
                        switch (this.dashboardNotice) {
                            case 'newOverview':
                                // delay handling the notice for 5 seconds (to give the dashboard some time to load)
                                timer(5 * 1000)
                                    .pipe(
                                        takeUntil(this.unsubscribe$),
                                        switchMap(() => {
                                            return this._toastService.show({
                                                intent: 'info',
                                                title: 'New layout!',
                                                message: `We're working on a new dashboard layout 🚀. Want to check it out?`,
                                                actions: {
                                                    primary: 'Yes',
                                                    secondary: 'Maybe later'
                                                },
                                                duration: 60 * 1000
                                            }).$action;
                                        })
                                    )
                                    .subscribe((action) => {
                                        if (action.name === 'primary') {
                                            this.router.navigate(['/overview']);
                                        }
                                    });
                                break;
                        }
                    }
                    this.isDataLoaded = true;
                    this.changeDetector.markForCheck();
                },
                (err) => {
                    console.error(
                        'Could not load grid with path: ' +
                            this.gridPath +
                            ' due to: ' +
                            err
                    );

                    this.isDataLoaded = true;
                    this.router.navigate([this.uiQuery.landingPage()]);
                }
            );
        });
        this.listenForSearchEvent();
    }

    ngOnDestroy() {
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }

    // Todo - extract to utility method - being used here and in panel-component.ts DRY
    getLayoutClass(sbLayout: SbLayout): string {
        const width = `col-${sbLayout.width || 12}`;
        if (sbLayout.width) {
            return width;
        } else {
            const widthSm = `col-sm-${sbLayout.widthSm || 12}`;
            const widthMd = `col-md-${sbLayout.widthMd || 6}`;
            const widthLg = `col-lg-${sbLayout.widthLg || 4}`;
            const widthXl = `col-xl-${sbLayout.widthXl || 4}`;
            const fixedAt = sbLayout.fixAtSm
                ? 'sb-p-col-fixed-small'
                : sbLayout.fixedAtMd
                ? 'sb-p-col-fixed-medium'
                : sbLayout.fixedAtLg
                ? 'sb-p-col-fixed-large'
                : sbLayout.fixedAtXl
                ? 'sb-p-col-fixed-huge'
                : '';
            return ['col-12', widthSm, widthMd, widthLg, widthXl, fixedAt].join(' ');
        }
    }

    hasPanelLayouts(panelLayout: PanelLayout): boolean {
        return panelLayout.panelLayouts != null && panelLayout.panelLayouts.length > 0;
    }
    hasTabbedPanels(panelLayout: PanelLayout): boolean {
        return panelLayout.tabbedPanels != null && panelLayout.tabbedPanels.length > 0;
    }

    trackByPanelId: TrackByFunction<PanelLayout> = (
        index: number,
        panelLayout: PanelLayout
    ) => {
        return panelLayout.panelId;
    };

    /**
     * Workaround to display a cancel button for a tabbed panel grid (assuming there are transaction-forms in it for now).
     */
    displayCancelButton(panelLayout: PanelLayout): boolean {
        return panelLayout.tabbedPanels?.length > 0;
    }

    handleTabChange(e) {
        const event: SbEvent = {
            source: 'DoIneedThisInThisCase',
            topic: SbEventTopic.TabPanelTabChange,
            payload: {
                tabIndex: e.index
            }
        };
        this.eventBus.emit(event);
    }

    goBack() {
        if (this.grid.parentGridPath) {
            this.router.navigate(['./', 'sb-grid', this.grid.parentGridPath]);
        }
    }

    cancel(event) {
        this.location.back();
    }

    /**
     * Listens for 'FormFiltered' event from 'portfolio-report-filter-form' to signal search completion
     * and updates this.isSearchDone, enabling visibility of result panels.
     */
    listenForSearchEvent() {
        this.eventBus
            .getObservable('FormFiltered')
            .pipe(
                debounceTime(300),
                takeWhile(() => !this.isSearchDone),
                takeUntil(this.unsubscribe$)
            )
            .subscribe(
                (event) => {
                    if (event && event.source === 'portfolio-report-filter-form') {
                        this.isSearchDone = true;
                    }
                },
                (error) => console.error(error)
            );
    }

    /**
     * Determines panel visibility based on search status at 'portfolio-report' page.
     * Default visibility is true, except for 'portfolio-report-search' panel, which is dependent on search completion.
     * @param panelId
     */
    isPanelVisible(panelId: string): boolean {
        if (this.grid.gridPath === 'portfolio-report') {
            return panelId === 'portfolio-report-search' || this.isSearchDone;
        }
        return true;
    }
}
